import React, { useState, useCallback } from 'react';
import {
    Card, Input, Radio, Select, Button, Space, Table, InputNumber as AntInputNumber,
    Typography, Row, Col, Divider, Modal
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import InputNumber from 'components/input-number';
import { formatCurrency } from 'utils/number';
import ProductPicker from 'components/pickers/product';
import TagPicker from 'components/pickers/product_tag';
import CustomerPicker from 'components/pickers/customer';
import { toSearchText } from 'utils/text';

const { Text } = Typography;

const PriceEditModal = ({ row, priceItems, onPriceItemChange, siblingVariants }) => {
    const [open, setOpen] = useState(false);
    const [editPrice, setEditPrice] = useState(0);
    const [editTiers, setEditTiers] = useState([]);

    const openModal = () => {
        setEditPrice(priceItems[row.id]?.original_price ?? row.original_price ?? 0);
        setEditTiers([...(priceItems[row.id]?.price_list ?? row.price_list ?? [])]);
        setOpen(true);
    };

    const handleOk = () => {
        onPriceItemChange(row.id, 'original_price', editPrice);
        onPriceItemChange(row.id, 'price_list', editTiers);
        setOpen(false);
    };

    const handleApplyToProduct = () => {
        (siblingVariants || []).forEach(v => {
            onPriceItemChange(v.id, 'original_price', editPrice);
            onPriceItemChange(v.id, 'price_list', editTiers);
        });
        onPriceItemChange(row.id, 'original_price', editPrice);
        onPriceItemChange(row.id, 'price_list', editTiers);
        setOpen(false);
    };

    const addTier = () => {
        if (editTiers.length > 0) {
            const last = editTiers[editTiers.length - 1];
            setEditTiers([...editTiers, { min: (last.max ?? last.min ?? 0) + 1, price: last.price }]);
        } else {
            setEditTiers([{ min: 2, price: editPrice }]);
        }
    };

    const updateTier = (i, field, val) => {
        const next = [...editTiers];
        next[i] = { ...next[i], [field]: val ?? 0 };
        setEditTiers(next);
    };

    const deleteTier = (i) => {
        const next = [...editTiers];
        next.splice(i, 1);
        setEditTiers(next);
    };

    const currentPrice = priceItems[row.id]?.original_price ?? row.original_price ?? 0;
    const currentTiers = priceItems[row.id]?.price_list ?? row.price_list ?? [];

    return (
        <>
            <div>
                <Space>
                    <Text>{formatCurrency(currentPrice)}</Text>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={openModal}
                        style={{ padding: 0 }}
                    />
                </Space>
                {currentTiers.map((tier, i) => (
                    <div key={i} style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                        Từ {tier.min}: {formatCurrency(tier.price)}
                    </div>
                ))}
            </div>
            <Modal
                title={`Sửa giá bán — ${row?.product?.name ?? ''} ${row.variant_title ?? ''}`}
                open={open}
                onCancel={() => setOpen(false)}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button type="dashed" onClick={addTier}>+ Thêm khoảng giá</Button>
                        <Space>
                            {siblingVariants && siblingVariants.length > 0 && (
                                <Button onClick={handleApplyToProduct}>Áp dụng cho biến thể khác</Button>
                            )}
                            <Button type="primary" onClick={handleOk}>Áp dụng</Button>
                            <Button onClick={() => setOpen(false)}>Huỷ</Button>
                        </Space>
                    </div>
                }
                width={680}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Row align="middle" gutter={8}>
                        <Col flex="120px"><Text>Giá bán mặc định</Text></Col>
                        <Col flex="auto">
                            <InputNumber
                                value={editPrice}
                                onChange={(v) => setEditPrice(v ?? 0)}
                                style={{ width: '100%' }}
                            />
                        </Col>
                    </Row>
                    {editTiers.length > 0 && (
                        <>
                        <Text type="secondary" style={{ fontSize: 13 }}>Giá theo số lượng</Text>
                        <Table
                            pagination={false}
                            size="small"
                            dataSource={editTiers}
                            rowKey={(_, i) => i}
                            columns={[
                                {
                                    title: 'Từ số lượng',
                                    render: (_, tier, i) => (
                                        <InputNumber
                                            value={tier.min}
                                            onChange={(v) => updateTier(i, 'min', v)}
                                            showCalculator={false}
                                            style={{ width: 100 }}
                                        />
                                    ),
                                },
                                {
                                    title: 'Giá',
                                    render: (_, tier, i) => (
                                        <InputNumber
                                            value={tier.price}
                                            onChange={(v) => updateTier(i, 'price', v)}
                                            style={{ width: 130 }}
                                        />
                                    ),
                                },
                                {
                                    title: '',
                                    width: 60,
                                    render: (_, __, i) => (
                                        <Button type="link" danger size="small" onClick={() => deleteTier(i)}>Xoá</Button>
                                    ),
                                },
                            ]}
                        />
                        </>
                    )}
                </Space>
            </Modal>
        </>
    );
};

const ModeOptions = [
    { label: 'Cố định (nhập tay)', value: 'manual' },
    { label: 'Tự động (chiết khấu)', value: 'auto' },
];

const ApplyToOptions = [
    { label: 'Tất cả sản phẩm', value: 'all' },
    { label: 'Theo tag', value: 'tag' },
    { label: 'Theo danh mục', value: 'category' },
    { label: 'Chọn thủ công', value: 'manual' },
];

const AutoRuleTypeOptions = [
    { label: '% giảm', value: 'percent' },
    { label: 'Giảm số tiền cố định', value: 'fixed_amount' },
];

const Desktop = ({ viewModel }) => {
    const {
        isEdit, name, setName,
        mode, setMode,
        applyTo, setApplyTo,
        tagIds, setTagIds,
        categoryIds, setCategoryIds,
        productIds, setProductIds,
        autoRule, setAutoRule,
        scopedVariants, priceItems, onPriceItemChange,
        tags, categories,
        applyAutoRule,
        tableId,
        customers, onAddCustomer, onRemoveCustomer,
        onSave, navigate,
    } = viewModel;

    const [searchText, setSearchText] = useState('');
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const handleAddCustomer = useCallback(async () => {
        if (!selectedCustomer?.id) return;

        const doAdd = async () => {
            await onAddCustomer?.(selectedCustomer);
            setSelectedCustomerId(null);
            setSelectedCustomer(null);
        };

        if (selectedCustomer.price_table_id && String(selectedCustomer.price_table_id) !== String(tableId)) {
            Modal.confirm({
                title: 'Khách hàng đang thuộc bảng giá khác',
                content: 'Khách hàng này đang áp dụng bảng giá khác. Bạn có muốn chuyển sang bảng giá hiện tại không?',
                okText: 'Xác nhận chuyển',
                cancelText: 'Huỷ',
                onOk: doAdd,
            });
            return;
        }

        await doAdd();
    }, [onAddCustomer, selectedCustomer, tableId]);

    const renderGiaGoc = (row) => {
        const tiers = row.price_list ?? [];
        return (
            <div>
                <div>{formatCurrency(row.original_price ?? 0)}</div>
                {tiers.map((tier, i) => (
                    <div key={i} style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                        Từ {tier.min}: {formatCurrency(tier.price)}
                    </div>
                ))}
            </div>
        );
    };

    const displayedVariants = React.useMemo(() => {
        if (!searchText.trim()) return scopedVariants;
        const q = toSearchText(searchText);
        return scopedVariants.filter(v => toSearchText(v?.product?.name ?? '').includes(q));
    }, [scopedVariants, searchText]);

    // Build rowSpan map: for each variant index, how many rows its product spans
    const rowSpanMap = React.useMemo(() => {
        const variants = displayedVariants;
        const map = {};
        let i = 0;
        while (i < variants.length) {
            const pid = variants[i]?.product_id;
            let count = 0;
            let j = i;
            while (j < variants.length && variants[j]?.product_id === pid) {
                count++;
                j++;
            }
            map[i] = count;
            for (let k = i + 1; k < j; k++) map[k] = 0;
            i = j;
        }
        return map;
    }, [displayedVariants]);

    const productNameCol = {
        title: 'Sản phẩm',
        width: 180,
        onCell: (_, index) => ({ rowSpan: rowSpanMap[index] ?? 1 }),
        render: (_, row) => row?.product?.name ?? '',
    };

    const autoColumns = [
        productNameCol,
        { title: 'Biến thể', dataIndex: 'variant_title', key: 'variant_title' },
        {
            title: 'Giá gốc',
            render: (_, row) => renderGiaGoc(row),
        },
        {
            title: 'Giá bán',
            render: (_, row) => {
                const tiers = row.price_list ?? [];
                return (
                    <div>
                        <Text>{formatCurrency(applyAutoRule(row.original_price ?? 0, autoRule))}</Text>
                        {tiers.map((tier, i) => (
                            <div key={i} style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                                Từ {tier.min}: {formatCurrency(applyAutoRule(tier.price, autoRule))}
                            </div>
                        ))}
                    </div>
                );
            },
        },
    ];

    const manualColumns = [
        productNameCol,
        { title: 'Biến thể', dataIndex: 'variant_title', key: 'variant_title' },
        {
            title: 'Giá gốc',
            render: (_, row) => renderGiaGoc(row),
        },
        {
            title: 'Giá bán',
            render: (_, row) => (
                <PriceEditModal
                    row={row}
                    priceItems={priceItems}
                    onPriceItemChange={onPriceItemChange}
                    siblingVariants={scopedVariants.filter(v => v.product_id === row.product_id && v.id !== row.id)}
                />
            ),
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/pricing')}>Quay lại</Button>
                <Text strong>{isEdit ? 'Chỉnh sửa bảng giá' : 'Tạo bảng giá mới'}</Text>
            </Space>

            {/* Card 1: General info */}
            <Card title="Thông tin chung">
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div>
                        <Text>Tên bảng giá</Text>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="VD: Bảng giá đại lý"
                            style={{ marginTop: 4 }}
                        />
                    </div>
                    <div>
                        <Text>Chế độ</Text>
                        <div style={{ marginTop: 4 }}>
                            <Radio.Group
                                options={ModeOptions}
                                value={mode}
                                onChange={(e) => setMode(e.target.value)}
                                optionType="button"
                            />
                        </div>
                    </div>
                    {mode === 'auto' && (
                        <Row gutter={8} align="middle">
                            <Col>
                                <Text>Loại chiết khấu:</Text>
                            </Col>
                            <Col>
                                <Select
                                    value={autoRule.type}
                                    onChange={(v) => setAutoRule(prev => ({ ...prev, type: v }))}
                                    options={AutoRuleTypeOptions}
                                    style={{ width: 200 }}
                                />
                            </Col>
                            <Col>
                                <AntInputNumber
                                    value={autoRule.value}
                                    min={0}
                                    max={autoRule.type === 'percent' ? 100 : undefined}
                                    onChange={(v) => setAutoRule(prev => ({ ...prev, value: v ?? 0 }))}
                                    addonAfter={autoRule.type === 'percent' ? '%' : '₫'}
                                    style={{ width: 160 }}
                                />
                            </Col>
                        </Row>
                    )}
                </Space>
            </Card>

            {/* Card 2: Scope */}
            <Card title="Phạm vi áp dụng">
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <div>
                        <Text>Áp dụng cho</Text>
                        <div style={{ marginTop: 4 }}>
                            <Select
                                value={applyTo}
                                onChange={setApplyTo}
                                options={ApplyToOptions}
                                style={{ width: 240 }}
                            />
                        </div>
                    </div>
                    {applyTo === 'tag' && (
                        <div>
                            <Text>Tag sản phẩm</Text>
                            <div style={{ marginTop: 4 }}>
                                <TagPicker
                                    mode="multiple"
                                    value={tagIds}
                                    onChange={setTagIds}
                                    style={{ width: '100%' }}
                                    placeholder="Chọn tag"
                                />
                            </div>
                        </div>
                    )}
                    {applyTo === 'category' && (
                        <div>
                            <Text>Danh mục</Text>
                            <div style={{ marginTop: 4 }}>
                                <Select
                                    mode="multiple"
                                    value={categoryIds}
                                    onChange={setCategoryIds}
                                    options={categories}
                                    style={{ width: '100%' }}
                                    placeholder="Chọn danh mục"
                                />
                            </div>
                        </div>
                    )}
                    {applyTo === 'manual' && (
                        <div>
                            <Text>Sản phẩm</Text>
                            <div style={{ marginTop: 4 }}>
                                <ProductPicker
                                    mode="multiple"
                                    value={productIds}
                                    onChange={setProductIds}
                                    style={{ width: '100%' }}
                                    placeholder="Chọn sản phẩm"
                                />
                            </div>
                        </div>
                    )}
                </Space>
            </Card>

            {/* Card 3: Product price table */}
            <Card title={`Danh sách sản phẩm (${displayedVariants.length}/${scopedVariants.length} biến thể)`}>
                <div style={{ marginBottom: 8 }}>
                    <Input
                        placeholder="Tìm tên sản phẩm..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        style={{ width: 280 }}
                    />
                </div>
                <Table
                    rowKey="id"
                    dataSource={displayedVariants}
                    columns={mode === 'auto' ? autoColumns : manualColumns}
                    pagination={{ pageSize: 50 }}
                    size="small"
                />
            </Card>

            {/* Card 4: Customers */}
            {isEdit && (
                <Card title={`Khách hàng áp dụng (${(customers || []).length})`}>
                    <Space style={{ marginBottom: 12 }}>
                        <div style={{ width: 360 }}>
                            <CustomerPicker
                                value={selectedCustomerId}
                                onChange={setSelectedCustomerId}
                                onCustomerChange={setSelectedCustomer}
                                placeholder="Chọn khách hàng"
                            />
                        </div>
                        <Button
                            type="primary"
                            onClick={handleAddCustomer}
                            disabled={!selectedCustomerId}
                        >
                            Thêm khách hàng
                        </Button>
                    </Space>
                    <Table
                        rowKey="id"
                        dataSource={customers || []}
                        pagination={false}
                        size="small"
                        columns={[
                            { title: 'SĐT', dataIndex: 'phone', width: 130 },
                            { title: 'Họ tên', dataIndex: 'full_name' },
                            {
                                title: '',
                                width: 60,
                                render: (_, row) => (
                                    <Button
                                        type="link"
                                        danger
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        onClick={() => onRemoveCustomer(row.id)}
                                    />
                                ),
                            },
                        ]}
                    />
                </Card>
            )}

            <Divider />
            <Space>
                <Button type="primary" onClick={onSave}>Lưu bảng giá</Button>
                <Button onClick={() => navigate('/pricing')}>Huỷ</Button>
            </Space>
        </Space>
    );
};

export default Desktop;
